import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Quote, ChevronDown, ChevronUp } from "lucide-react";
import { SkipToContent } from "@/components/site/SkipToContent";
import { StickyHeader } from "@/components/site/StickyHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PageHero } from "@/components/site/PageHero";
import { Reveal, Kicker, WfButton, Tag } from "@/components/site/primitives";
import { FARMERS_HERO, FARMERS_INTRO, FARMER_SPOTLIGHTS, FARMERS_MORE } from "@/content/farmers";
import { scrollToId } from "@/lib/scrollTo";
import { cn } from "@/lib/utils";

/* ---------------- Intro ---------------- */
const IntroSection = () => (
  <section id="farmers-intro" aria-labelledby="farmers-intro-heading" className="wf-section bg-[var(--wf-cream)]">
    <div className="wf-container">
      <Reveal className="max-w-3xl">
        <Kicker>{FARMERS_INTRO.eyebrow}</Kicker>
        <h2 id="farmers-intro-heading" className="mt-4 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl lg:text-5xl">
          {FARMERS_INTRO.title}
        </h2>
        <div className="mt-5 space-y-4">
          {FARMERS_INTRO.paragraphs.map((p, i) => (
            <p key={i} className="max-w-[62ch] text-base leading-7 text-[var(--wf-soil)]">{p}</p>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);

/* ---------------- Farmer Spotlights grid ---------------- */
const yearStarted = (f) => (f.facts || []).find((x) => /year started/i.test(x.label))?.value;

const SpotlightsGrid = () => (
  <section id="farmer-spotlights" aria-labelledby="farmer-spotlights-heading" className="wf-section bg-white">
    <div className="wf-container">
      <Reveal className="max-w-2xl">
        <Kicker>Meet them</Kicker>
        <h2 id="farmer-spotlights-heading" className="mt-4 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl lg:text-5xl">Farmer Spotlights</h2>
      </Reveal>
      <ul className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="farmer-spotlights-grid">
        {FARMER_SPOTLIGHTS.map((f, i) => (
          <Reveal key={f.id} delay={0.06 * i} as="li" className="h-full">
            <a
              href={`#${f.id}`}
              onClick={(e) => { e.preventDefault(); scrollToId(f.id); }}
              data-testid={`farmer-spotlight-card-${f.id}`}
              className="group flex h-full flex-col items-center rounded-sm border border-[var(--wf-border)] bg-[var(--wf-paper)] p-7 text-center shadow-[var(--wf-shadow-sm)] transition-[box-shadow,transform] duration-300 hover:shadow-[var(--wf-shadow)] motion-safe:hover:-translate-y-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
            >
              <img src={f.portrait} alt="" loading="lazy" className="h-28 w-28 rounded-full object-cover ring-4 ring-[var(--wf-wheat)]" />
              <span className="mt-4 font-serif text-xl font-semibold text-[var(--wf-ink)] group-hover:text-[var(--wf-maroon)]">{f.name}</span>
              <span className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground"><MapPin className="h-3.5 w-3.5" aria-hidden="true" /> From {f.origin}</span>
              {yearStarted(f) && (
                <span className="mt-2 inline-flex items-center rounded-full bg-[var(--wf-cream)] px-3 py-1 text-xs font-semibold text-[var(--wf-green-text)]">Started {yearStarted(f)}</span>
              )}
              <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--wf-maroon)]">Read their story <ChevronDown className="h-4 w-4 transition-transform group-hover:translate-y-0.5" aria-hidden="true" /></span>
            </a>
          </Reveal>
        ))}
      </ul>
      <p className="mt-6 text-sm text-muted-foreground">More farmer stories coming soon.</p>
    </div>
  </section>
);

/* ---------------- Spotlight ---------------- */
const PREVIEW_PARAGRAPHS = 2;

const Spotlight = ({ farmer, index }) => {
  const [expanded, setExpanded] = useState(false);
  const flip = index % 2 === 1;
  const shown = expanded ? farmer.paragraphs : farmer.paragraphs.slice(0, PREVIEW_PARAGRAPHS);
  const hasMore = farmer.paragraphs.length > PREVIEW_PARAGRAPHS;
  const storyId = `${farmer.id}-story`;

  return (
    <article
      id={farmer.id}
      aria-labelledby={`${farmer.id}-heading`}
      data-testid={`farmer-spotlight-${farmer.id}`}
      className={cn("wf-section scroll-mt-24", flip ? "bg-white" : "bg-[var(--wf-cream)]")}
    >
      <div className="wf-container">
        <div className={cn("grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14")}>
          {/* Visual column */}
          <Reveal className={cn("lg:col-span-5", flip && "lg:order-2")}>
            <figure className="relative overflow-hidden rounded-sm shadow-[var(--wf-shadow)]">
              <img src={farmer.portrait} alt={farmer.portraitAlt} loading="lazy" className="aspect-[4/5] w-full object-cover" />
              <figcaption className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 bg-gradient-to-t from-black/70 to-transparent p-5 text-white">
                <span>
                  <span className="block font-serif text-xl font-semibold">{farmer.name}</span>
                  <span className="flex items-center gap-1 text-xs text-white/85"><MapPin className="h-3 w-3" aria-hidden="true" /> From {farmer.origin}</span>
                </span>
              </figcaption>
            </figure>

            {farmer.gallery?.length > 0 && (
              <ul className={cn("mt-4 grid gap-3", farmer.gallery.length > 1 ? "grid-cols-3" : "grid-cols-1")}>
                {farmer.gallery.map((g) => (
                  <li key={g.src}>
                    <figure className="group overflow-hidden rounded-sm border border-[var(--wf-border)] bg-[var(--wf-paper)]">
                      <img src={g.src} alt={g.alt} loading="lazy" className={cn("w-full object-cover", farmer.gallery.length > 1 ? "aspect-square" : "aspect-[16/9]")} />
                      <figcaption className="px-3 py-2 text-[11px] leading-4 text-muted-foreground">{g.caption}</figcaption>
                    </figure>
                  </li>
                ))}
              </ul>
            )}
          </Reveal>

          {/* Story column */}
          <Reveal delay={0.1} className={cn("lg:col-span-7", flip && "lg:order-1")}>
            <div className="flex flex-wrap items-center gap-2">
              <Kicker>{farmer.kicker}</Kicker>
              {farmer.farm && <Tag tone="green">{farmer.farm}</Tag>}
            </div>
            <h2 id={`${farmer.id}-heading`} className="mt-4 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl lg:text-[2.6rem] lg:leading-[1.1]">
              {farmer.headline}
            </h2>

            <blockquote className="mt-6 flex gap-3 rounded-sm border-l-4 border-[var(--wf-maroon)] bg-[var(--wf-wheat)]/60 p-5">
              <Quote className="mt-1 h-5 w-5 shrink-0 text-[var(--wf-maroon)]" aria-hidden="true" />
              <p className="font-serif text-lg leading-7 text-[var(--wf-ink)] sm:text-xl sm:leading-8">{`\u201C${farmer.quote}\u201D`}</p>
            </blockquote>

            <dl className="mt-6 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
              {farmer.facts.map((f) => (
                <div key={f.label} className="border-t border-[var(--wf-border)] pt-3">
                  <dt className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--wf-green-text)]">{f.label}</dt>
                  <dd className="mt-1 text-sm leading-6 text-[var(--wf-soil)]">{f.value}</dd>
                </div>
              ))}
            </dl>

            <div id={storyId} className="mt-8 space-y-4">
              {shown.map((p, i) => (
                <p key={i} className="max-w-[68ch] text-base leading-7 text-[var(--wf-soil)]">{p}</p>
              ))}
            </div>

            {hasMore && (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                aria-expanded={expanded}
                aria-controls={storyId}
                data-testid={`farmer-${farmer.id}-toggle`}
                className="mt-6 inline-flex min-h-11 items-center gap-2 rounded-sm border border-[var(--wf-maroon)] px-6 py-2.5 text-sm font-semibold text-[var(--wf-maroon)] transition-colors duration-200 hover:bg-[var(--wf-maroon)] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
              >
                {expanded ? "Show less" : `Read ${farmer.shortName}\u2019s full story`}
                {expanded ? <ChevronUp className="h-4 w-4" aria-hidden="true" /> : <ChevronDown className="h-4 w-4" aria-hidden="true" />}
              </button>
            )}
          </Reveal>
        </div>
      </div>
    </article>
  );
};

/* ---------------- More coming ---------------- */
const MoreSection = () => (
  <section aria-labelledby="farmers-more-heading" className="bg-white py-16 sm:py-20 lg:py-24">
    <div className="wf-container">
      <div className="relative overflow-hidden rounded-sm bg-[var(--wf-farm-green)] px-6 py-14 text-center sm:px-10 sm:py-16">
        <div aria-hidden="true" className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/10 blur-2xl" />
        <div className="relative mx-auto max-w-2xl">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[rgba(255,253,248,0.85)]">Meet them in person</span>
          <h2 id="farmers-more-heading" className="mt-4 font-serif text-3xl font-semibold leading-[1.1] text-white sm:text-4xl">{FARMERS_MORE.title}</h2>
          <p className="mx-auto mt-4 max-w-xl text-base leading-7 text-[rgba(255,253,248,0.88)]">{FARMERS_MORE.body}</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <WfButton as={Link} to={FARMERS_MORE.cta.href} variant="light" withArrow className="px-7" data-testid="farmers-more-markets-button">{FARMERS_MORE.cta.label}</WfButton>
            <WfButton as={Link} to={FARMERS_MORE.secondary.href} className="border border-white/60 bg-white/10 px-7 text-white backdrop-blur-sm hover:bg-white/20" data-testid="farmers-more-support-button">{FARMERS_MORE.secondary.label}</WfButton>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ---------------- Page ---------------- */
export default function MeetTheFarmersPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <>
      <SkipToContent />
      <StickyHeader overlay linkToHome />
      <main id="content" tabIndex={-1} className="outline-none">
        <PageHero {...FARMERS_HERO} testid="farmers-hero" />
        <IntroSection />
        <SpotlightsGrid />
        {FARMER_SPOTLIGHTS.map((f, i) => (
          <Spotlight key={f.id} farmer={f} index={i} />
        ))}
        <MoreSection />
      </main>
      <SiteFooter />
    </>
  );
}
